import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useStory, STEPS } from '../../context/StoryContext';
import { api } from '../../services/api';
import { Sparkles, BookOpen, Heart, Gift } from 'lucide-react';

// URLs con fondo transparente
const TURRIS_LOGO = '/logo-turris.svg';
const MOCKUP_BOY = 'https://customer-assets.emergentagent.com/job_sant-jordi-baker/artifacts/fjownnc2_libro%203d%20nen.png';
const MOCKUP_GIRL = 'https://customer-assets.emergentagent.com/job_sant-jordi-baker/artifacts/o88y9spx_libro%203d%20noia.png';
// Mobile background (vertical/square)
const BG_IMAGE_MOBILE = 'https://customer-assets.emergentagent.com/job_sant-jordi-baker/artifacts/8uf9xw1t_ChatGPT%20Image%2027%20mar%202026%2C%2011_14_30.png';
// Desktop/Tablet background (panoramic)
const BG_IMAGE_DESKTOP = '/background-desktop.png';

export default function LandingScreen() {
  const { goToStep, setProtagonistType } = useStory();
  const [activeBook, setActiveBook] = useState(0);

  useEffect(() => {
    api.trackEvent('landing_viewed');
    
    const interval = setInterval(() => {
      setActiveBook(prev => (prev === 0 ? 1 : 0));
    }, 3500);
    
    return () => clearInterval(interval);
  }, []);

  const handleStart = () => {
    api.trackEvent('start_clicked');
    goToStep(STEPS.PROTAGONIST_TYPE);
  };

  const handleBookClick = (type) => {
    api.trackEvent(`book_${type}_clicked`);
    setProtagonistType(type);
    goToStep(STEPS.NAME);
  };

  return (
    <div className="min-h-screen h-screen flex flex-col overflow-hidden relative">
      {/* Mobile background image (shown on small screens) */}
      <div 
        className="absolute inset-0 bg-cover bg-no-repeat md:hidden"
        style={{ 
          backgroundImage: `url(${BG_IMAGE_MOBILE})`,
          backgroundPosition: 'center top',
        }}
      />
      {/* Desktop/Tablet background image (shown on md and larger) */}
      <div 
        className="absolute inset-0 hidden md:block"
        style={{ 
          backgroundImage: `url(${BG_IMAGE_DESKTOP})`,
          backgroundPosition: 'center center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      />
      {/* Gradient overlay to soften the background - more transparent to show dragon */}
      <div 
        className="absolute inset-0"
        style={{ 
          background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 40%, rgba(255,255,255,0.15) 70%, rgba(255,255,255,0.25) 100%)'
        }}
      />

      {/* Header with logo - sin pastilla */}
      <header className="relative z-20 pt-3 lg:pt-4 pb-1 flex justify-center">
        <motion.img
          src={TURRIS_LOGO}
          alt="Turris"
          className="h-16 sm:h-20 lg:h-24 drop-shadow-lg"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        />
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-2 lg:py-0">
        
        {/* Hero section */}
        <motion.div
          className="text-center mb-2 lg:mb-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg mb-2 lg:mb-3"
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <BookOpen className="w-4 h-4 lg:w-5 lg:h-5" style={{ color: '#D32F2F' }} />
            <span className="text-xs lg:text-sm font-semibold" style={{ color: '#1a365d' }}>
              Un conte únic per a cada nen
            </span>
            <Sparkles className="w-3 h-3 lg:w-4 lg:h-4" style={{ color: '#FFD700' }} />
          </motion.div>
          
          <h1 
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-1"
            style={{ 
              color: '#1a365d',
              textShadow: '2px 2px 8px rgba(255,255,255,0.8)'
            }}
          >
            Crea el teu conte
          </h1>
          <h2 
            className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold"
            style={{ 
              color: '#D32F2F',
              textShadow: '2px 2px 8px rgba(255,255,255,0.8)'
            }}
          >
            de Sant Jordi
          </h2>
        </motion.div>

        {/* Books showcase - FLOTANDO sin badges */}
        <motion.div
          className="relative w-full max-w-3xl mb-2 lg:mb-3"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="flex justify-center items-end gap-0 sm:gap-4">
            {/* Left book - Noi */}
            <motion.div
              className="relative cursor-pointer"
              onClick={() => handleBookClick('noi')}
              onMouseEnter={() => setActiveBook(0)}
              animate={{
                scale: activeBook === 0 ? 1 : 0.82,
                x: activeBook === 0 ? 30 : 0,
                y: activeBook === 0 ? -15 : 0,
                zIndex: activeBook === 0 ? 10 : 1,
                rotate: activeBook === 0 ? -4 : -10,
              }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              whileHover={{ scale: activeBook === 0 ? 1.08 : 0.9, y: -25 }}
              whileTap={{ scale: 0.95 }}
              style={{
                filter: activeBook === 0 
                  ? 'drop-shadow(0 35px 35px rgba(0,0,0,0.45))' 
                  : 'drop-shadow(0 15px 20px rgba(0,0,0,0.2))'
              }}
            >
              <img
                src={MOCKUP_BOY}
                alt="El pa de Sant Jordi del Max"
                className="w-36 sm:w-44 lg:w-52 xl:w-56"
                style={{ 
                  opacity: activeBook === 0 ? 1 : 0.7,
                }}
              />
            </motion.div>

            {/* Right book - Noia */}
            <motion.div
              className="relative cursor-pointer"
              onClick={() => handleBookClick('noia')}
              onMouseEnter={() => setActiveBook(1)}
              animate={{
                scale: activeBook === 1 ? 1 : 0.82,
                x: activeBook === 1 ? -30 : 0,
                y: activeBook === 1 ? -15 : 0,
                zIndex: activeBook === 1 ? 10 : 1,
                rotate: activeBook === 1 ? 4 : 10,
              }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              whileHover={{ scale: activeBook === 1 ? 1.08 : 0.9, y: -25 }}
              whileTap={{ scale: 0.95 }}
              style={{
                filter: activeBook === 1 
                  ? 'drop-shadow(0 35px 35px rgba(0,0,0,0.45))' 
                  : 'drop-shadow(0 15px 20px rgba(0,0,0,0.2))'
              }}
            >
              <img
                src={MOCKUP_GIRL}
                alt="El pa de Sant Jordi de la Noa"
                className="w-36 sm:w-44 lg:w-52 xl:w-56"
                style={{ 
                  opacity: activeBook === 1 ? 1 : 0.7,
                }}
              />
            </motion.div>
          </div>
          
          {/* Book selector dots */}
          <div className="flex justify-center gap-3 mt-3 lg:mt-4">
            <button
              onClick={() => setActiveBook(0)}
              className="w-2.5 h-2.5 lg:w-3 lg:h-3 rounded-full transition-all duration-300"
              style={{ 
                backgroundColor: activeBook === 0 ? '#3B82F6' : 'rgba(0,0,0,0.2)',
                transform: activeBook === 0 ? 'scale(1.4)' : 'scale(1)',
                boxShadow: activeBook === 0 ? '0 2px 10px rgba(59, 130, 246, 0.6)' : 'none'
              }}
            />
            <button
              onClick={() => setActiveBook(1)}
              className="w-2.5 h-2.5 lg:w-3 lg:h-3 rounded-full transition-all duration-300"
              style={{ 
                backgroundColor: activeBook === 1 ? '#EC4899' : 'rgba(0,0,0,0.2)',
                transform: activeBook === 1 ? 'scale(1.4)' : 'scale(1)',
                boxShadow: activeBook === 1 ? '0 2px 10px rgba(236, 72, 153, 0.6)' : 'none'
              }}
            />
          </div>
        </motion.div>

        {/* Info badges */}
        <motion.div
          className="flex flex-wrap justify-center gap-2 lg:gap-3 mb-3 lg:mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="flex items-center gap-2 bg-white/95 px-3 lg:px-4 py-1.5 lg:py-2 rounded-full shadow-md backdrop-blur-sm">
            <Gift className="w-3.5 h-3.5 lg:w-4 lg:h-4" style={{ color: '#2A9D8F' }} />
            <span className="text-xs lg:text-sm font-semibold" style={{ color: '#1a365d' }}>100% Gratuït</span>
          </div>
          <div className="flex items-center gap-2 bg-white/95 px-3 lg:px-4 py-1.5 lg:py-2 rounded-full shadow-md backdrop-blur-sm">
            <Heart className="w-3.5 h-3.5 lg:w-4 lg:h-4" style={{ color: '#D32F2F' }} fill="#D32F2F" />
            <span className="text-xs lg:text-sm font-semibold" style={{ color: '#1a365d' }}>Per imprimir i pintar</span>
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.button
          data-testid="start-button"
          onClick={handleStart}
          className="group relative px-10 sm:px-12 lg:px-14 py-4 sm:py-5 lg:py-5 rounded-full text-lg sm:text-xl lg:text-xl font-bold text-white overflow-hidden"
          style={{ 
            background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
            boxShadow: '0 20px 50px rgba(211, 47, 47, 0.5), 0 8px 20px rgba(211, 47, 47, 0.4), inset 0 -3px 0 rgba(0,0,0,0.2)'
          }}
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          whileHover={{ scale: 1.05, y: -5 }}
          whileTap={{ scale: 0.98 }}
        >
          {/* Shimmer effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
            animate={{ x: [-400, 400] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
          />
          <span className="relative flex items-center gap-2 lg:gap-3">
            <Sparkles className="w-5 h-5 lg:w-6 lg:h-6" />
            Crea el teu conte!
          </span>
        </motion.button>

        <motion.p
          className="mt-2 text-xs lg:text-sm font-semibold"
          style={{ color: '#4a5568' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          ✨ En només 2 minuts!
        </motion.p>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-2 lg:py-3 text-center">
        <div className="bg-white/80 backdrop-blur-sm inline-block px-4 lg:px-6 py-1.5 lg:py-2 rounded-full">
          <p className="text-xs lg:text-sm font-medium" style={{ color: '#4a5568' }}>
            Una experiència de <span className="font-bold" style={{ color: '#1a365d' }}>Turris</span> · El Pa de Sant Jordi
          </p>
        </div>
      </footer>
    </div>
  );
}
